import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './ItemDetailsDisplay.css';
import CertificateDetails from './CertificateDetails'; // Import the new component
import CertificateQRCode from './CertificateQRCode'; // Import QR code component

// Define the shape of the data for a certificate
interface CertificateData {
    name: string;
    description: string;
    image: string | null; // Assuming image URL can be null
    attributes?: { trait_type: string; value: string | number }[];

}

// Define the shape for a status history entry
interface StatusHistoryEntry {
    statusName: string;
    timestamp: number; // Assuming timestamp is a number (e.g., Unix timestamp)
    notes: string;
    latitude?: string;
    longitude?: string;
}

// Define the shape of the 'item' prop
interface ItemData {
    id: string;
    certificate: CertificateData;
    statusHistory: StatusHistoryEntry[];
    initialCertificateURI: string;
    currentCertificateURI: string;
    historicalCertificateURIs: string[];
}

// Define the props interface for ItemDetailsDisplay
interface ItemDetailsDisplayProps {
    item: ItemData;
    userAddress: string | null; // To check if user is connected
    isCertificateContractOwner: boolean; // To check for cert ownership
    isTrackerContractOwner: boolean; // To check for ownership
    onNavigateToUpdateStatus: () => void;
    onNavigateToUpdateCertificate: () => void;
    onNavigateToCertificateHistory: () => void;
    onUseAsTemplate: () => void;
}

const ItemDetailsDisplay: FC<ItemDetailsDisplayProps> = ({ item, userAddress, isCertificateContractOwner, isTrackerContractOwner, onNavigateToUpdateStatus, onNavigateToUpdateCertificate, onNavigateToCertificateHistory, onUseAsTemplate }) => {
    const { t } = useTranslation();
    const [selectedStatus, setSelectedStatus] = useState<StatusHistoryEntry | null>(null);


    const handleTimelineDotClick = (status: StatusHistoryEntry) => {
        setSelectedStatus(status);
    };

    const closeModal = () => {
        setSelectedStatus(null);
    };

    const getLocationParts = (attributes?: { trait_type: string; value: string | number }[]) => {
        const locationAttribute = attributes?.find(attr => attr.trait_type === "Location");
        if (locationAttribute && typeof locationAttribute.value === 'string') {
            const parts = locationAttribute.value.split(',').map(part => parseFloat(part.trim()));
            if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
                return { latitude: parts[0], longitude: parts[1] };
            }
        }
        return null;
    };

    const location = getLocationParts(item.certificate.attributes);

    return (
        <div className="item-details-container">
            <h2>{t('itemDetails.title', { id: item.id })}</h2>
            <div className="status-history">
                <h3>{t('itemDetails.historyTitle')}</h3>
                {(() => {
                    const initialStatusName = "Raw material sourcing"; // Match contract enum string
                    let processedHistory: StatusHistoryEntry[] = [];

                    const firstActualTimestamp = item.statusHistory && item.statusHistory.length > 0
                        ? item.statusHistory[0].timestamp
                        : Math.floor(Date.now() / 1000); // Fallback if no history

                    // Always start with a "Created" status
                    processedHistory.push({
                        statusName: initialStatusName,
                        timestamp: firstActualTimestamp, // Use timestamp of first actual event or now
                        notes: "Item created/registered."
                    });

                    if (item.statusHistory && item.statusHistory.length > 0) {
                        // Add the rest of the history, skipping if the first actual status is also "Created"
                        // to avoid duplicate "Created" entries if the backend already logs it.
                        item.statusHistory.forEach(entry => {
                            if (entry.statusName !== initialStatusName || processedHistory.length > 1 || processedHistory[0].timestamp !== entry.timestamp) {
                                processedHistory.push(entry);
                            }
                        });
                    }

                    return (
                        <div className="timeline-container">
                            {processedHistory.map((entry, index) => {
                                const isCurrent = index === processedHistory.length - 1;
                                return (
                                    <div
                                        key={index}
                                        className={`timeline-point ${isCurrent ? 'current' : ''}`}
                                        onClick={() => handleTimelineDotClick(entry)}
                                    >
                                        <div className="timeline-dot"></div>
                                        <div className="timeline-label">{entry.statusName}</div>
                                        {index < processedHistory.length - 1 && <div className="timeline-line"></div>}
                                    </div>
                                );
                            })}
                        </div>
                    );
                })()}
            </div>
            {/* Certificate details and QR code side by side */}
            <div className="certificate-qr-container">
                <CertificateDetails
                    certificate={item.certificate}
                    tokenId={item.id}
                    currentUri={item.currentCertificateURI}
                    onUseAsTemplate={userAddress && isCertificateContractOwner ? onUseAsTemplate : undefined}
                    useAsTemplateLabel={t('itemDetails.useAsTemplateButton')}
                />
                <CertificateQRCode tokenId={item.id} />
            </div>

            {selectedStatus && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="modal-close-button" onClick={closeModal}>×</button>
                        <h3>{t('itemDetails.modal.title')}</h3>
                        <div className="certificate-details-modal">
                            <h4>{t('itemDetails.modal.certificateLabel', { name: item.certificate.name })}</h4>
                            <p>{item.certificate.description}</p>
                            {item.certificate.image && (
                                <img src={item.certificate.image} alt={item.certificate.name} className="modal-image-preview" />
                            )}
                            {location && (
                                <p>
                                    <strong>{t('itemDetails.modal.locationLabel')}</strong> {location.latitude.toFixed(5)}, {location.longitude.toFixed(5)}
                                    <a href={`https://www.openstreetmap.org/?mlat=${location.latitude}&mlon=${location.longitude}#map=15/${location.latitude}/${location.longitude}`} target="_blank" rel="noopener noreferrer" style={{ marginLeft: '10px' }}>
                                        {t('itemDetails.modal.viewOnMap')}
                                    </a>
                                </p>
                            )}
                        </div>
                        <p><strong>{t('itemDetails.modal.statusLabel')}</strong> {selectedStatus.statusName}</p>
                        <p><strong>{t('itemDetails.modal.timestampLabel')}</strong> {new Date(selectedStatus.timestamp * 1000).toLocaleString()}</p>
                        <p><strong>{t('itemDetails.modal.notesLabel')}</strong> {selectedStatus.notes || t('itemDetails.modal.notesNa')}</p>
                        {selectedStatus.latitude && selectedStatus.longitude && selectedStatus.latitude !== "N/A" && (
                            <p>
                                <strong>{t('itemDetails.modal.locationAtStepLabel')}</strong> {selectedStatus.latitude}, {selectedStatus.longitude}
                                <a href={`https://www.openstreetmap.org/?mlat=${selectedStatus.latitude}&mlon=${selectedStatus.longitude}#map=15/${selectedStatus.latitude}/${selectedStatus.longitude}`} target="_blank" rel="noopener noreferrer" style={{ marginLeft: '10px' }}>
                                    {t('itemDetails.modal.viewOnMap')}
                                </a>
                            </p>
                        )}
                    </div>
                </div>
            )}

            <div className="action-buttons-container">
                {userAddress && isTrackerContractOwner && (
                    <button
                        onClick={onNavigateToUpdateStatus}
                        className="update-status-nav-button action-button" // Use existing classes
                    >
                        {t('itemDetails.updateItemStatusButton')}
                    </button>
                )}
                {userAddress && isCertificateContractOwner && (
                    <button
                        onClick={onNavigateToUpdateCertificate}
                        className="update-cert-nav-button action-button"
                    >
                        {t('itemDetails.updateCertificateButton')}
                    </button>
                )}
                {/* This button navigates to the new history page */}
                <button
                    onClick={onNavigateToCertificateHistory}
                    className="history-nav-button action-button"
                >
                    {t('itemDetails.showHistoryButton')}
                </button>
            </div>
        </div>
    );
};

export default ItemDetailsDisplay;
